import { Box, Flex, Grid, Quote, Text } from '@radix-ui/themes'
import { type TFunction } from 'i18next'
import type * as Rect from 'react'

import {
	Bio,
	Compatibility,
	// Gift,
	Happy,
	Innovative,
	MapPin_1,
	PackageOpen,
	Product_3,
	Recycling_1,
	// Recycling_2,
	Shop,
	Together,
	TransportFree,
	Vegan_2,
	World_1,
} from '#app/components/icon'
import { type GetProducts } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-products'
import { type Locale } from '#app/models/locale'
import { InfoBlock } from '#app/routes/($lang)._index/info-block'
import { ProductsSlider } from '#app/routes/($lang)._index/products-slider'

/* -------------------------------------------------------------------------------------------------
 * InfoGrid
 * -----------------------------------------------------------------------------------------------*/

type InfoGridProps = {
	products: GetProducts
	locale: Locale
	t: TFunction<string, undefined>
}

const InfoGrid: Rect.FC<InfoGridProps> = ({ locale, t, products }) => {
	return (
		<>
			<Flex
				direction={{ initial: 'column', md: 'row' }}
				justify="center"
				align="center"
				mt="7"
				gap="2"
				className="text-center"
			>
				<Quote className="!text-2xl !text-gray-11 sm:!text-3xl">
					{t('common.quote')}
				</Quote>
				<Text
					color="gray"
					weight="medium"
					size={{
						initial: '5',
						sm: '6',
					}}
				>
					- {t('label.aristoteles')}
				</Text>
			</Flex>
			<Grid
				columns={{ initial: '1', md: '2' }}
				gap="6"
				className="place-items-stretch"
			>
				{[
					{ Icon: World_1, path: '/blog/f1513524-e280-44f6-85da-c55893061990' }, // Environment
					{
						Icon: Recycling_1,
						path: '/blog/b72f5dd9-6d5c-43ed-a4eb-37f3e234e191',
					}, // Recycling
					{
						Icon: Together,
						path: '/blog/06d63cde-0d93-46a3-bddd-e03988804da8?page=3&more=%2Fde%2Fblog%3Fpage%3D3',
					}, // Together
					{
						Icon: PackageOpen,
						path: '/blog/f1513524-e280-44f6-85da-c55893061990#outer-packaging',
					}, // Secondary Packaging
					{
						Icon: MapPin_1,
						path: '/blog/f1513524-e280-44f6-85da-c55893061990',
					}, // Regional
				].map(({ Icon, path }, i) => (
					<InfoBlock
						key={i}
						Icon={Icon}
						idx={i}
						title={t(`common.blog${i}Title`)}
						text={t(`common.blog${i}Text`)}
						path={path}
						locale={locale}
					/>
				))}
			</Grid>
			<Box mt="5">
				<ProductsSlider products={products} />
			</Box>
			<Grid
				columns={{ initial: '1', md: '2' }}
				gap="5"
				className="place-items-stretch"
			>
				{[
					{
						Icon: Product_3,
						path: '/blog/0107224c-272d-4041-be05-a1eda464cbb2',
					}, // Products
					{ Icon: Vegan_2, path: '/products?taxon=vegan' }, // Vegan
					{
						Icon: Bio,
						path: '/blog/f1513524-e280-44f6-85da-c55893061990#bio-qualities-oo-good-for-yourself-and-the-environment',
					}, // Organic
					{ Icon: Compatibility, path: '' }, // Compatibility
					{ Icon: Innovative, path: '/products?taxon=innovative' }, // Innovative
				].map(({ Icon, path }, i) => (
					<InfoBlock
						key={i}
						Icon={Icon}
						idx={i}
						title={t(`common.blog${i + 5}Title`)}
						text={t(`common.blog${i + 5}Text`)}
						path={path}
						locale={locale}
					/>
				))}
			</Grid>
			<Box mt="5">
				<ProductsSlider products={products} />
			</Box>
			<Grid
				columns={{ initial: '1', md: '2' }}
				gap="5"
				className="place-items-stretch"
			>
				{[
					{ Icon: Shop, path: '/products' }, // Shop
					// { Icon: Recycling_2, path: '/blog/90e0d643-1208-41a4-92e3-6720bd4db69e', }, // Subscription
					{ Icon: TransportFree, path: '/shipment' }, // Free Shipping
					// { Icon: Gift, path: '/products?taxon=gift_ideas' }, // Gift Ideas
					{ Icon: Happy, path: '/profile#invite' }, // Satisfaction
				].map(({ Icon, path }, i) => (
					<InfoBlock
						key={i}
						Icon={Icon}
						idx={i}
						title={t(`common.blog${i + 10}Title`)}
						text={t(`common.blog${i + 10}Text`)}
						path={path}
						locale={locale}
					/>
				))}
			</Grid>
		</>
	)
}
InfoGrid.displayName = 'InfoGrid'

/* -----------------------------------------------------------------------------------------------*/

export { InfoGrid }
